import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { PaymentMethodAddNewItemOption } from 'components/payment-method-add-new-item-option';
import PaymentMethodOption from 'components/payment-method-option';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import noop from 'utils/noop';

import { ADD_PREPAID_CARD } from './constants';
import { PaymentMethodOptions } from './payment-method-options';
import {
  PaymentMethodSelect,
  StyledAddNewCreditCardIcon,
  StyledAddPaymentMethodIcon,
  StyledChequeGourmetCardIcon,
  StyledDisplayToggle,
  StyledIdealCardIcon,
  StyledPaypalCardIcon,
  StyledPrepaidCardIcon,
  StyledSodexoCardIcon,
} from './styled';
import { CurrentSelectedMethodType, IPaymentMethodProps } from './types';
import { classifyPaymentMethods, getCurrentSelectedMethodType } from './utils';

function PaymentMethod({
  methods,
  selectedMethodId,
  handleSelect,
  isAddCardOpen = false,
  openAddCardOnClick,
  isAddGiftCardMethodSelected = false,
  isAdyenIdealMethodSelected = false,
  isPaycometPaypalMethodSelected = false,
  setIsAddGiftCardMethodSelected = noop,
  setIsAdyenIdealMethodSelected,
  openPaycometPaypalOnClick,
  isPaycometSodexoMethodSelected,
  setIsPaycometSodexoMethodSelected,
  isPaycometChequeGourmetMethodSelected,
  setIsPaycometChequeGourmetMethodSelected,
  onResult,
  serverOrder,
}: IPaymentMethodProps) {
  const { formatMessage } = useIntl();
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const enableAdyenIdealPayment = useFlag(LaunchDarklyFlag.ENABLE_ADYEN_IDEAL_PAYMENT);
  const checkoutCashLimit = useFlag(LaunchDarklyFlag.CHECKOUT_CASH_PRICE_LIMIT);
  const totalAmount = Number(serverOrder?.cart.totalCents);
  const hideCashPayment = checkoutCashLimit && totalAmount > checkoutCashLimit ? true : false;

  const [collapsed, setCollapsed] = useState(true);

  const { selectedMethod, unSelectedMethods, prepaidPaymentMethod } = classifyPaymentMethods(
    methods,
    selectedMethodId,
    hideCashPayment
  );

  const showAdyenIdealPaymentOption = enableAdyenIdealPayment && !isAdyenIdealMethodSelected;

  const showAddNewGiftCardOption =
    !isAddGiftCardMethodSelected && enableGiftCard && !prepaidPaymentMethod;
  const showAddNewCardOption = !isAddCardOpen;

  const isCurrentSelectedMethodOptionClickable =
    showAddNewGiftCardOption ||
    showAddNewCardOption ||
    showAdyenIdealPaymentOption ||
    !!unSelectedMethods.length;

  function toggleCollapsed() {
    setCollapsed(currentCollapsed => !currentCollapsed);
  }
  function getSelectedMethodClickHandler() {
    return isCurrentSelectedMethodOptionClickable ? toggleCollapsed : undefined;
  }

  const SelectedPaymentMethodOptionItem = selectedMethod ? (
    <PaymentMethodOption
      method={selectedMethod}
      onClick={getSelectedMethodClickHandler()}
      disableHover={collapsed}
      disableMethod={!!selectedMethod.prepaid && !enableGiftCard}
      hideBorder
      fromCheckout
    />
  ) : null;
  const SelectedAddNewPaymentMethodOptionItem = (
    <PaymentMethodAddNewItemOption
      data-testid="add-new-payment-method"
      text={formatMessage({ id: 'addNewPaymentMethod' })}
      Icon={<StyledAddPaymentMethodIcon />}
      onClick={getSelectedMethodClickHandler()}
      selected
    />
  );
  const SelectedAddNewCardOptionItem = (
    <PaymentMethodAddNewItemOption
      data-testid="add-new-card"
      text={formatMessage({ id: 'addNewCard' })}
      Icon={<StyledAddNewCreditCardIcon />}
      onClick={getSelectedMethodClickHandler()}
      selected
    />
  );
  const SelectedAddNewGiftCardOptionItem = (
    <PaymentMethodAddNewItemOption
      data-testid="add-gift-card"
      text={formatMessage({ id: ADD_PREPAID_CARD })}
      Icon={<StyledPrepaidCardIcon />}
      onClick={getSelectedMethodClickHandler()}
      selected
    />
  );
  const SelectedPaypalOptionItem = (
    <PaymentMethodAddNewItemOption
      data-testid="payment-method-paypal"
      text={formatMessage({ id: 'paypal' })}
      Icon={<StyledPaypalCardIcon />}
      onClick={getSelectedMethodClickHandler()}
      selected
    />
  );
  const SelectedIdealOptionItem = (
    <PaymentMethodAddNewItemOption
      data-testid="payment-method-ideal"
      text={formatMessage({ id: 'ideal' })}
      Icon={<StyledIdealCardIcon />}
      onClick={getSelectedMethodClickHandler()}
      selected
    />
  );

  const SelectedSodexoOptionItem = (
    <PaymentMethodAddNewItemOption
      data-testid="payment-method-sodexo"
      text={formatMessage({ id: 'addNewPaymentSodexo' })}
      Icon={<StyledSodexoCardIcon />}
      onClick={getSelectedMethodClickHandler()}
      selected
    />
  );

  const SelectedChequeGourmetOptionItem = (
    <PaymentMethodAddNewItemOption
      data-testid="payment-method-cheque-gourmet"
      text={formatMessage({ id: 'addNewPaymentChequeGourmet' })}
      Icon={<StyledChequeGourmetCardIcon />}
      onClick={getSelectedMethodClickHandler()}
      selected
    />
  );

  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected: isAddCardOpen,
    isAdyenIdealMethodSelected,
    isPaycometPaypalMethodSelected,
    isPaycometSodexoMethodSelected,
    isPaycometChequeGourmetMethodSelected,
    selectedMethodExist: !!selectedMethod,
  });
  const CurrentSelectedMethodMap = {
    [CurrentSelectedMethodType.SELECTED_PAYMENT_METHOD_OPTION]: SelectedPaymentMethodOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD]: SelectedAddNewPaymentMethodOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_CARD]: SelectedAddNewCardOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_GIFT_CARD]: SelectedAddNewGiftCardOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_PAYMENT_PAYPAL]: SelectedPaypalOptionItem,
    [CurrentSelectedMethodType.PAY_VIA_IDEAL]: SelectedIdealOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_PAYMENT_SODEXO]: SelectedSodexoOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_PAYMENT_CHEQUE_GOURMET]: SelectedChequeGourmetOptionItem,
  };
  const CurrentSelectedMethod = CurrentSelectedMethodMap[currentSelectedMethodType];

  return (
    <>
      <PaymentMethodSelect data-testid="payment-methods">
        {CurrentSelectedMethod}
        {isCurrentSelectedMethodOptionClickable && (
          <StyledDisplayToggle
            collapsed={collapsed}
            onClick={toggleCollapsed}
            onCollapseKeyPress={toggleCollapsed}
          />
        )}

        <PaymentMethodOptions
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          unSelectedMethods={unSelectedMethods}
          handleSelect={handleSelect}
          setIsAddNewCardOptionSelected={openAddCardOnClick}
          setIsAddNewGiftCardOptionSelected={setIsAddGiftCardMethodSelected}
          setIsAdyenIdealOptionSelected={setIsAdyenIdealMethodSelected}
          setIsPaycometPaypalOptionSelected={openPaycometPaypalOnClick}
          setIsPaycometSodexoOptionSelected={setIsPaycometSodexoMethodSelected}
          setIsPaycometChequeGourmetOptionSelected={setIsPaycometChequeGourmetMethodSelected}
          showAddNewGiftCardOption={showAddNewGiftCardOption}
          showAddNewCardOption={showAddNewCardOption}
          showAdyenIdealPaymentOption={showAdyenIdealPaymentOption}
          onResult={onResult}
          serverOrder={serverOrder}
        />
      </PaymentMethodSelect>
    </>
  );
}

export default PaymentMethod;
